import { HStack, IconButton, Text, Flex, Box, Menu, MenuButton, MenuList, MenuItem, Avatar, Divider, Button, Breadcrumb, BreadcrumbItem, BreadcrumbLink, useColorMode, VStack, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Badge, Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, PopoverArrow, PopoverCloseButton, Checkbox, Spinner, CheckboxGroup } from '@chakra-ui/react'
import { Menu as MenuIcon, Mail, Bell, LogOut, User, Moon, Sun, Circle, Clock, Coffee, UserCheck, DoorOpen, GraduationCap, PhoneOutgoing, AlertTriangle, BookOpen } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  useAvailabilityCatalog,
  setAvailabilityCache,
  availabilityDurationMinutes,
  availabilityStatusColor,
  type UserAvailabilityStatus,
} from '@/lib/userAvailability'
import { User as AppUser } from '@/types'
import { useLocation, useNavigate } from 'react-router-dom'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'
import { useUserStatusStore } from '@/store/userStatus'
import { useAuthStore } from '@/store/auth'
import { useUnsavedChangesStore } from '@/store/unsavedChanges'
import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { canAssignSupportAgent } from '@/lib/utils/roleHelper'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { getInboxEmails } from '@/lib/services/inboxService'
import { useInboxNotificationsStore } from '@/store/inboxNotifications'
import { useTaskNotificationsStore } from '@/store/taskNotifications'
import {
  notificationService,
  type AppNotification,
} from '@/lib/services/notificationService'
import {
  formatTicketStatusLabel,
  getLogoutAssignmentPreview,
  type LogoutAssignmentPreviewTicket,
} from '@/lib/services/logoutAssignmentService'
import { TYPO_BODY, TYPO_CAPTION, TYPO_TITLE } from '@/lib/typography'

const INBOX_POLL_MS = 15_000
const TASK_NOTIF_POLL_MS = 30_000
const NOTIF_SUBJECT_MAX = 100
const HEADER_ICON_SIZE = 16
const HEADER_MENU_ICON_SIZE = 15

function compactText(value: string | null | undefined): string {
  return (value || '').replace(/\s+/g, ' ').trim()
}

function truncateText(value: string, max: number): string {
  if (value.length <= max) return value
  return `${value.slice(0, max - 1)}…`
}

function relativeTime(iso?: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffSec = Math.round((Date.now() - then) / 1000)
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  return `${Math.floor(diffSec / 86400)}d ago`
}

interface HeaderProps {
  onOpen: () => void
  user: AppUser | null
  onLogout: (retainTicketIds?: number[]) => void
  phoneLabel: string
  phoneTone: 'green' | 'blue' | 'orange' | 'red' | 'gray'
  onPhoneOpen: () => void
}

export function Header({ onOpen, user, onLogout, phoneLabel, phoneTone, onPhoneOpen }: HeaderProps) {
  const colors = useThemeColors()
  const location = useLocation()
  const pathname = location.pathname
  const segments = (pathname || '/').split('/').filter(Boolean)
  const toLabel = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const title = segments.length > 0 ? toLabel(segments[0]) : 'Dashboard'
  const crumbs = segments.map((seg, idx) => ({
    label: toLabel(seg),
    href: '/' + segments.slice(0, idx + 1).join('/'),
  }))
  const navigate = useNavigate()
  const requestLeave = useUnsavedChangesStore((s) => s.requestLeave)
  const guardedNavigate = useCallback(
    (path: string) => {
      requestLeave(() => navigate(path))
    },
    [navigate, requestLeave],
  )
  const { colorMode, toggleColorMode } = useColorMode()
  const userStatus = useUserStatusStore((s) => s.status)
  const statusChangedAt = useUserStatusStore((s) => s.statusChangedAt)
  const setUserStatus = useUserStatusStore((s) => s.setStatus)
  const { headerOptions, definitions: availabilityDefinitions } = useAvailabilityCatalog()

  useEffect(() => {
    if (availabilityDefinitions.length > 0) {
      setAvailabilityCache(availabilityDefinitions)
    }
  }, [availabilityDefinitions])
  const [isLogoutOpen, setIsLogoutOpen] = useState(false)
  const [logoutTickets, setLogoutTickets] = useState<LogoutAssignmentPreviewTicket[]>([])
  const [retainIds, setRetainIds] = useState<number[]>([])
  const [logoutPreviewLoading, setLogoutPreviewLoading] = useState(false)
  const [logoutPreviewFailed, setLogoutPreviewFailed] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const authUser = useAuthStore((s) => s.user)
  const canViewAgentStatus = canAssignSupportAgent(authUser)

  const openLogoutDialog = useCallback(() => {
    setIsLogoutOpen(true)
    setLogoutTickets([])
    setRetainIds([])
    setLogoutPreviewFailed(false)
    setLogoutPreviewLoading(true)
    getLogoutAssignmentPreview()
      .then((tickets) => {
        setLogoutTickets(tickets)
        setRetainIds(tickets.filter((t) => t.default_retain).map((t) => t.id))
        setLogoutPreviewFailed(false)
      })
      .catch(() => {
        setLogoutTickets([])
        setRetainIds([])
        setLogoutPreviewFailed(true)
      })
      .finally(() => setLogoutPreviewLoading(false))
  }, [])

  const closeLogoutDialog = useCallback(() => {
    if (isLoggingOut) return
    setIsLogoutOpen(false)
    setLogoutTickets([])
    setRetainIds([])
    setLogoutPreviewFailed(false)
  }, [isLoggingOut])

  const retainCount = retainIds.length
  const releaseCount = Math.max(0, logoutTickets.length - retainCount)
  const allTicketsSelected =
    logoutTickets.length > 0 && retainIds.length === logoutTickets.length
  const someTicketsSelected = retainIds.length > 0 && !allTicketsSelected

  const toggleSelectAllTickets = useCallback(() => {
    if (allTicketsSelected) {
      setRetainIds([])
      return
    }
    setRetainIds(logoutTickets.map((ticket) => ticket.id))
  }, [allTicketsSelected, logoutTickets])

  const confirmLogout = useCallback(async () => {
    setIsLoggingOut(true)
    try {
      // Preview failure → omit body so server applies status defaults.
      if (logoutPreviewFailed) {
        await onLogout()
      } else {
        await onLogout(retainIds)
      }
      setIsLogoutOpen(false)
    } finally {
      setIsLoggingOut(false)
    }
  }, [logoutPreviewFailed, onLogout, retainIds])
  const { showError, showSuccess } = useToastNotification()
  const unreadCount = useInboxNotificationsStore((s) => s.unread_count)
  const unreadCountText = String(unreadCount ?? 0)
  const unreadDigits = unreadCountText.length
  const badgePx = unreadDigits <= 1 ? 0 : unreadDigits === 2 ? 1 : 2
  const badgeMinW = unreadDigits <= 1 ? '16px' : unreadDigits === 2 ? '20px' : '28px'
  const setInboxMeta = useInboxNotificationsStore((s) => s.setMeta)
  const taskUnreadCount = useTaskNotificationsStore((s) => s.unread_count)
  const setTaskUnreadCount = useTaskNotificationsStore((s) => s.setUnreadCount)
  const [taskNotifications, setTaskNotifications] = useState<AppNotification[]>([])
  const [taskNotifLoading, setTaskNotifLoading] = useState(false)
  const inboxTotalRef = useRef(0)
  const inboxUnreadRef = useRef(0)
  const inboxTopIdRef = useRef<number | null>(null)
  const inboxFirstPollRef = useRef(true)

  /** Poll inbox meta app-wide so the bell badge stays current on any page */
  useEffect(() => {
    const tick = async () => {
      try {
        const res = await getInboxEmails({ page: 1, per_page: 1 })
        const latest = res.data[0]
        const unread = res.meta.unread_count
        if (!inboxFirstPollRef.current) {
          const hasMetaIncrease = unread > inboxUnreadRef.current
          const hasNewTopId =
            Boolean(
              latest &&
                inboxTopIdRef.current !== null &&
                latest.id > inboxTopIdRef.current,
            )
          if (hasNewTopId || hasMetaIncrease) {
            const latestSubject = compactText(latest?.subject) || '(No subject)'
            const latestFrom = compactText(latest?.from_name || latest?.from_email)
            const detailParts = [
              `Subject: ${truncateText(latestSubject, NOTIF_SUBJECT_MAX)}`,
              latestFrom ? `From: ${latestFrom}` : null,
            ].filter(Boolean)
            showSuccess('New email received', detailParts.join(' · '))
          }
        } else {
          inboxFirstPollRef.current = false
        }
        inboxTotalRef.current = res.meta.total
        inboxUnreadRef.current = unread
        inboxTopIdRef.current = latest ? latest.id : null
        setInboxMeta(unread, res.meta.total, latest?.id ?? null)
      } catch {
        /* ignore poll errors */
      }
    }
    void tick()
    const id = window.setInterval(() => void tick(), INBOX_POLL_MS)
    return () => window.clearInterval(id)
  }, [setInboxMeta, showSuccess])

  const refreshTaskNotifications = useCallback(async () => {
    try {
      const [count, items] = await Promise.all([
        notificationService.unreadCount('task_'),
        notificationService.listMine({ per_page: 15, type: 'task_' }),
      ])
      setTaskUnreadCount(count)
      setTaskNotifications(items.filter((n) => String(n.type || '').startsWith('task_')))
    } catch {
      /* ignore poll errors */
    }
  }, [setTaskUnreadCount])

  useEffect(() => {
    void refreshTaskNotifications()
    const id = window.setInterval(() => void refreshTaskNotifications(), TASK_NOTIF_POLL_MS)
    return () => window.clearInterval(id)
  }, [refreshTaskNotifications])

  const openTaskNotification = useCallback(
    async (notification: AppNotification) => {
      try {
        if (!notification.is_read) {
          await notificationService.markRead(notification.id)
          setTaskUnreadCount(Math.max(0, taskUnreadCount - 1))
          setTaskNotifications((prev) =>
            prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
          )
        }
      } catch {
        /* still navigate */
      }
      const parentId = notification.meta?.parent_ticket_id
      const taskId = notification.meta?.child_ticket_id
      if (parentId && taskId) {
        guardedNavigate(`/tickets/${parentId}/view?task=${taskId}`)
        return
      }
      if (notification.url) {
        guardedNavigate(notification.url)
      }
    },
    [guardedNavigate, setTaskUnreadCount, taskUnreadCount]
  )

  const handleMarkAllTaskRead = useCallback(async () => {
    setTaskNotifLoading(true)
    try {
      await notificationService.markAllRead()
      setTaskUnreadCount(0)
      setTaskNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
    } catch (err) {
      showError(err as Error)
    } finally {
      setTaskNotifLoading(false)
    }
  }, [setTaskUnreadCount, showError])

  // Auto-switch to online after timed statuses expire
  useEffect(() => {
    const durationMinutes = availabilityDurationMinutes(userStatus)
    if (!statusChangedAt || durationMinutes == null) return

    const checkInterval = setInterval(() => {
      const changedAt = new Date(statusChangedAt)
      const now = new Date()
      const elapsedMinutes = Math.floor((now.getTime() - changedAt.getTime()) / (1000 * 60))

      if (elapsedMinutes >= durationMinutes) {
        setUserStatus('online', (error) => {
          showError(error)
        })
      }
    }, 60000) // Check every minute

    return () => clearInterval(checkInterval)
  }, [statusChangedAt, userStatus, setUserStatus, showError, availabilityDefinitions])

  const avatarBgColor = useMemo(
    () => availabilityStatusColor(userStatus),
    [userStatus, availabilityDefinitions]
  )

  const headerStatusIcons: Record<string, LucideIcon> = useMemo(
    () => ({
      online: Circle,
      break_15_1: Clock,
      break_15_2: Clock,
      break_60: Coffee,
      restroom: DoorOpen,
      coaching: GraduationCap,
      email_handling: Mail,
      outbound_call: PhoneOutgoing,
      system_issue: AlertTriangle,
      training: BookOpen,
    }),
    []
  )
  
  return (
    <Box bg={colors.cardBg} borderBottom="1px" borderColor={colors.border} px={5} py={3}>
      <Flex justify="space-between" align="center">
        <HStack align="start" spacing={2}>
          <IconButton
            aria-label="Open menu"
            icon={<MenuIcon size={HEADER_ICON_SIZE} />}
            variant="ghost"
            size="sm"
            display="inline-flex"
            onClick={onOpen}
          />
          <Box display={{ base: 'none', md: 'block' }}>
            <Text fontSize={TYPO_TITLE} fontWeight="semibold" lineHeight="short">{title}</Text>
            {segments.length > 0 && (
              <Breadcrumb fontSize={TYPO_CAPTION} color={colors.text} separator="/">
                <BreadcrumbItem key="home">
                  <BreadcrumbLink
                    onClick={(e) => {
                      e.preventDefault()
                      guardedNavigate('/dashboard')
                    }}
                  >
                    Home
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {crumbs.map((c, i) => (
                  <BreadcrumbItem key={c.href} isCurrentPage={i === crumbs.length - 1}>
                    {i === crumbs.length - 1 ? (
                      <Text>{c.label}</Text>
                    ) : (
                      <BreadcrumbLink
                        onClick={(e) => {
                          e.preventDefault()
                          guardedNavigate(c.href)
                        }}
                      >
                        {c.label}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                ))}
              </Breadcrumb>
            )}
          </Box>
        </HStack>

        <HStack spacing={1}>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<PhoneOutgoing size={HEADER_ICON_SIZE} />}
            onClick={onPhoneOpen}
            aria-label={`Open phone, ${phoneLabel}`}
            data-testid="phone-header-button"
          >
            <Text as="span" display={{ base: 'none', lg: 'inline' }}>Phone</Text>
            <Badge ml={{ base: 0, lg: 2 }} colorScheme={phoneTone} textTransform="none">
              {phoneLabel}
            </Badge>
          </Button>
          <IconButton 
            aria-label="Toggle color mode" 
            icon={colorMode === 'light' ? <Moon size={HEADER_ICON_SIZE} /> : <Sun size={HEADER_ICON_SIZE} />} 
            variant="ghost"
            size="sm"
            onClick={toggleColorMode}
          />
          <Box position="relative" display="inline-flex">
            <IconButton
              aria-label="Unread case threads"
              icon={<Mail size={HEADER_ICON_SIZE} strokeWidth={2} aria-hidden />}
              variant="ghost"
              size="sm"
              onClick={() => guardedNavigate('/case-threads')}
            />
            {unreadCount > 0 && (
              <Badge
                position="absolute"
                top="0px"
                right="-15px"
                colorScheme="red"
                variant="solid"
                borderRadius="full"
                fontSize="0.65rem"
                minW={badgeMinW}
                h="16px"
                px={badgePx}
                display="flex"
                alignItems="center"
                justifyContent="center"
                pointerEvents="none"
              >
                {unreadCountText}
              </Badge>
            )}
          </Box>
          <Popover placement="bottom-end" isLazy>
            <PopoverTrigger>
              <Box position="relative" display="inline-flex">
                <IconButton
                  aria-label="Task notifications"
                  icon={<Bell size={HEADER_ICON_SIZE} strokeWidth={2} aria-hidden />}
                  variant="ghost"
                  size="sm"
                />
                {taskUnreadCount > 0 ? (
                  <Badge
                    position="absolute"
                    top="0px"
                    right="-15px"
                    colorScheme="orange"
                    variant="solid"
                    borderRadius="full"
                    fontSize="0.65rem"
                    minW="16px"
                    h="16px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    pointerEvents="none"
                  >
                    {taskUnreadCount > 99 ? '99+' : taskUnreadCount}
                  </Badge>
                ) : null}
              </Box>
            </PopoverTrigger>
            <PopoverContent w="360px" maxW="90vw">
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader fontWeight="600">
                <HStack justify="space-between" pr={6}>
                  <Text>Task notifications</Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => void handleMarkAllTaskRead()}
                    isLoading={taskNotifLoading}
                    isDisabled={taskUnreadCount === 0}
                  >
                    Mark all read
                  </Button>
                </HStack>
              </PopoverHeader>
              <PopoverBody p={0} maxH="360px" overflowY="auto">
                {taskNotifications.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" p={4}>
                    No task notifications yet.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={0} divider={<Divider />}>
                    {taskNotifications.map((notification) => (
                      <Box
                        key={notification.id}
                        as="button"
                        textAlign="left"
                        px={4}
                        py={3}
                        bg={notification.is_read ? 'transparent' : 'orange.50'}
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => void openTaskNotification(notification)}
                      >
                        <Text fontSize="sm" fontWeight={notification.is_read ? '500' : '700'}>
                          {notification.title}
                        </Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={2}>
                          {notification.message}
                        </Text>
                        <Text fontSize="xs" color="gray.400" mt={1}>
                          {relativeTime(notification.created_at)}
                        </Text>
                      </Box>
                    ))}
                  </VStack>
                )}
              </PopoverBody>
            </PopoverContent>
          </Popover>
          {canViewAgentStatus && (
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Team Overview"
                icon={<UserCheck size={HEADER_ICON_SIZE} />}
                variant="ghost"
                size="sm"
              />
              <MenuList fontSize={TYPO_BODY}>
                <MenuItem
                  icon={<UserCheck size={HEADER_MENU_ICON_SIZE} />}
                  onClick={() => guardedNavigate('/agent-status')}
                >
                  Agent Status
                </MenuItem>
              </MenuList>
            </Menu>
          )}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              size="sm"
              fontSize={TYPO_BODY}
              leftIcon={<Avatar size="xs" name={user?.user_login || ''} bg={avatarBgColor} color="white" />}
            >
              {user?.user_login ? user.user_login.split(' ')[0] : ''}
            </MenuButton>
            <MenuList fontSize={TYPO_BODY}>
              <Box px={2} py={2}>
                <Text fontSize={TYPO_CAPTION} fontWeight="600" textTransform="uppercase" letterSpacing="wider" color={colors.textSecondary} mb={2}>
                  User Status
                </Text>
                <VStack align="stretch" spacing={0}>
                  {headerOptions.map(({ code, label }) => {
                    const Icon = headerStatusIcons[code] ?? Circle
                    return (
                      <MenuItem
                        key={code}
                        onClick={() => {
                          setUserStatus(code as UserAvailabilityStatus, (error) => {
                            showError(error)
                          })
                        }}
                        bg={userStatus === code ? colors.sidebarActiveBg : 'transparent'}
                        color={userStatus === code ? colors.heading : colors.text}
                        icon={<Icon size={HEADER_MENU_ICON_SIZE} />}
                      >
                        {label}
                      </MenuItem>
                    )
                  })}
                </VStack>
              </Box>
              <Divider />
              <MenuItem
                icon={<User size={HEADER_MENU_ICON_SIZE} />}
                onClick={() => guardedNavigate('/profile')}
              >
                Profile
              </MenuItem>
              <Divider />
              <MenuItem icon={<LogOut size={HEADER_MENU_ICON_SIZE} />} onClick={openLogoutDialog}>
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <AlertDialog
        isOpen={isLogoutOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeLogoutDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW={logoutTickets.length > 0 ? 'lg' : 'md'}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Logout
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={logoutTickets.length > 0 || logoutPreviewLoading ? 3 : 0}>
                Are you sure you want to logout? You will need to sign in again to access your account.
              </Text>
              {logoutPreviewLoading && (
                <HStack py={2} spacing={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500">Loading assigned tickets…</Text>
                </HStack>
              )}
              {!logoutPreviewLoading && logoutPreviewFailed && (
                <Text fontSize="sm" color="gray.500">
                  Could not load ticket assignments. On-hold and resolved-incomplete tickets will stay assigned by default; open tickets will be released.
                </Text>
              )}
              {!logoutPreviewLoading && !logoutPreviewFailed && logoutTickets.length > 0 && (
                <Box>
                  <Text fontSize="sm" mb={2}>
                    Select tickets to keep assigned. Unchecked tickets will become unassigned.
                  </Text>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    {retainCount} will stay assigned · {releaseCount} will go unassigned
                  </Text>
                  <Checkbox
                    mb={2}
                    isChecked={allTicketsSelected}
                    isIndeterminate={someTicketsSelected}
                    onChange={toggleSelectAllTickets}
                    isDisabled={isLoggingOut}
                  >
                    Select all
                  </Checkbox>
                  <CheckboxGroup
                    value={retainIds.map(String)}
                    onChange={(values) => {
                      setRetainIds((values as string[]).map((v) => Number(v)))
                    }}
                  >
                    <VStack align="stretch" spacing={2} maxH="240px" overflowY="auto">
                      {logoutTickets.map((ticket) => (
                        <Checkbox
                          key={ticket.id}
                          value={String(ticket.id)}
                          alignItems="flex-start"
                          isDisabled={isLoggingOut}
                        >
                          <Box>
                            <Text fontSize="sm" fontWeight="medium">
                              {ticket.ticket_number || `#${ticket.id}`}
                              {ticket.status ? (
                                <Text as="span" fontWeight="normal" color="gray.500">
                                  {' '}· {formatTicketStatusLabel(ticket.status)}
                                </Text>
                              ) : null}
                            </Text>
                            {ticket.subject ? (
                              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                {ticket.subject}
                              </Text>
                            ) : null}
                          </Box>
                        </Checkbox>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                </Box>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeLogoutDialog} isDisabled={isLoggingOut}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => { void confirmLogout() }}
                ml={3}
                isLoading={isLoggingOut}
                isDisabled={logoutPreviewLoading}
              >
                Logout
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default Header

